import { cardFullscreen } from "../../../shared/cards/CardFullscreen";
import { createCardArt } from "../../../shared/cards/cardFace";
import type { Game } from "../game/Game";
import { dragAimMode } from "../game/cards/dragAim";
import { keywordsForVerse } from "../game/cards/keywords";
import type { CardInstance, VerseDef } from "../game/cards/types";

const DRAG_THRESHOLD_PX = 10;
/** In-flight card sits below-left of the cursor so the aim point stays clear. */
const FLY_SCALE = 0.86;
const FLY_GAP_X = 14;
const FLY_GAP_Y = 20;
/** ~80ms settle — eases out of the stack, then tracks without lag. */
const FLY_LERP_TAU = 0.08;

/**
 * Embedded iframe or known in-app browsers (X/Twitter, IG, FB, …).
 * Their chrome often steals vertical swipes / long-presses meant for card play.
 */
export function isConstrainedPlayShell(): boolean {
  try {
    if (window.self !== window.top) return true;
  } catch {
    // Cross-origin parent → definitely framed
    return true;
  }
  const ua = navigator.userAgent || "";
  if (
    /\bTwitter\b/i.test(ua) ||
    /\bTwitterAndroid\b/i.test(ua) ||
    /\bFBAN\b|\bFBAV\b|\bFB_IAB\b/i.test(ua) ||
    /\bInstagram\b/i.test(ua)
  ) {
    return true;
  }
  return false;
}

/** Player-facing drag aim (placeables / restore call out the verb). */
function dragHintForCard(def: VerseDef): string {
  if (def.kind === "placeable") {
    return "Drag onto an empty slot to plant a Prayer";
  }
  if (def.selector === "restorePrayer") {
    return "Drag onto your Prayer to mend it";
  }
  if (def.selector === "gatherPrayers") {
    return "Drag onto a keep to Gather residual Prayers into it";
  }
  if (def.selector === "convertLie") {
    return "Drag onto a weakened lie to Redeem it";
  }
  if (def.layAside) {
    return "Drop onto a hand card to lay it aside, or onto the board to auto-pick";
  }
  if (def.wipeConfusion) {
    return "Drop anywhere — casts down Confusion";
  }
  const aim = dragAimMode(def);
  if (aim === "global") return "Drag onto any slot (or a lie)";
  if (aim === "layer") return "Drag onto a layer";
  return "Drag onto a lie";
}

type PointerDragState = {
  pointerId: number;
  instanceId: string;
  /** Snapshot for tap-to-study (hand may rebuild before pointerup). */
  def: VerseDef;
  el: HTMLElement;
  startX: number;
  startY: number;
  active: boolean;
  /** Slot the card launched from — put it back when docking / ending. */
  homeSlot: HTMLElement | null;
  homeW: number;
  homeH: number;
  pointerX: number;
  pointerY: number;
  flyX: number;
  flyY: number;
  flyScale: number;
  /** Pointer is over the hand dock — card is slotted, still held. */
  overHand: boolean;
  /** Suppress the following click (art/title study) after a drag. */
  suppressClick: boolean;
};

export class HandUi {
  private dragId: string | null = null;
  private dirty = true;
  /** Hand card waiting to proclaim after speech lock */
  private queuedId: string | null = null;
  private pointerDrag: PointerDragState | null = null;
  /** Ignore study clicks until this timestamp (ms) after a pointer drag. */
  private suppressStudyUntil = 0;
  private docPointerBound = false;

  private readonly onDocPointerMove = (e: PointerEvent): void => {
    this.handlePointerMove(e);
  };
  private readonly onDocPointerUp = (e: PointerEvent): void => {
    this.endPointer(e, true);
  };
  private readonly onDocPointerCancel = (e: PointerEvent): void => {
    this.endPointer(e, false);
  };
  /** Snapshot at pointerdown — avoid closing over a stale Game in handlers. */
  private pointerDragGame: Game | null = null;
  private flyRaf = 0;
  private lastFlyTs = 0;

  constructor(
    private readonly root: HTMLElement,
    private readonly handlers: {
      onDragStart: (instanceId: string) => void;
      onDragMove: (clientX: number, clientY: number) => void;
      onDragEnd: () => void;
      onDropAt: (clientX: number, clientY: number, instanceId: string) => void;
      onDropOnCard?: (playedId: string, targetId: string) => void;
    },
  ) {}

  getDragId(): string | null {
    return this.dragId;
  }

  private cardAtPoint(
    clientX: number,
    clientY: number,
    exceptId: string,
  ): string | null {
    const stack = document.elementsFromPoint(clientX, clientY);
    for (const el of stack) {
      if (!(el instanceof HTMLElement)) continue;
      const card = el.closest<HTMLElement>(".card[data-instance-id]");
      const id = card?.dataset.instanceId;
      if (id && id !== exceptId) return id;
    }
    return null;
  }

  setQueuedId(id: string | null): void {
    if (this.queuedId === id) {
      this.root.querySelectorAll<HTMLElement>(".card").forEach((el) => {
        el.classList.toggle("queued", !!id && el.dataset.instanceId === id);
      });
      return;
    }
    this.queuedId = id;
    this.dirty = true;
  }

  getQueuedId(): string | null {
    return this.queuedId;
  }

  invalidate(): void {
    this.dirty = true;
  }

  render(game: Game): void {
    // Only rebuild when hand contents change — not every UI tick
    if (!this.dirty) {
      this.syncCosts(game);
      return;
    }
    // Don't tear down the hand mid-gesture (including pre-threshold press)
    if (this.pointerDrag) {
      this.syncCosts(game);
      return;
    }
    this.dirty = false;

    const handSize = game.scenario.handSize;
    this.root.style.setProperty("--hand-slots", String(handSize));
    this.root.innerHTML = "";

    // Fan math: outer cards tilt away from center (CSS reads --fan-rot / --fan-x).
    const fanCenter = (handSize - 1) / 2;
    const fanStepDeg = handSize <= 3 ? 7 : handSize <= 4 ? 6 : 5;

    for (let i = 0; i < handSize; i++) {
      const card = game.hand[i];
      const slot = document.createElement("div");
      slot.className = "card-slot";
      const rot = (i - fanCenter) * fanStepDeg;
      slot.style.setProperty("--fan-rot", `${rot}deg`);
      // Opposite of tilt: ease outer faces back in so corners aren’t clipped.
      slot.style.setProperty("--fan-x", `${(-rot * 0.7).toFixed(2)}px`);
      // Left→right stack; hover/focus still elevates via CSS z-index.
      slot.style.zIndex = String(i + 1);

      if (!card) {
        slot.classList.add("empty");
        const inner = document.createElement("div");
        inner.className = "empty-slot-inner";
        const label = document.createElement("span");
        label.className = "empty-label";
        label.textContent = "—";
        label.title = "Plays refill the hand automatically";
        inner.append(label);
        slot.appendChild(inner);
      } else {
        slot.appendChild(this.renderCard(card, game));
      }
      this.root.appendChild(slot);
    }
  }

  private syncCosts(game: Game): void {
    const playing = game.status === "playing";
    const paused = game.paused;

    this.root.querySelectorAll<HTMLElement>(".card").forEach((el) => {
      const id = el.dataset.instanceId;
      const card = game.hand.find((c) => c.instanceId === id);
      if (!card) return;
      el.classList.toggle("paused-block", paused);
      el.classList.toggle("queued", !!this.queuedId && id === this.queuedId);
      const empowered = game.isEmpowerHighlighted(card.def);
      el.classList.toggle("empowered", empowered);
      el.draggable = false;
      void playing;
      void paused;
    });
  }

  private openStudy(def: VerseDef): void {
    if (performance.now() < this.suppressStudyUntil) return;
    const speak = def.speakSeconds ?? 2.5;
    const kws = keywordsForVerse(def);
    const detailRows: { label: string; value: string }[] = [
      { label: "Speak", value: `${speak.toFixed(1)}s` },
      { label: "Kind", value: def.kind },
      { label: "Category", value: def.category },
    ];
    if (def.strongVs?.length) {
      detailRows.push({
        label: "Meets",
        value: def.strongVs
          .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
          .join(" / "),
      });
    }
    if (def.preferLayer) {
      detailRows.push({ label: "Layer", value: def.preferLayer });
    }
    if (def.lockLayer) {
      detailRows.push({ label: "Lock", value: def.lockLayer });
    }
    cardFullscreen.show({
      verseId: def.id,
      reference: def.reference,
      category: def.category,
      speakSeconds: speak,
      metaLine: `${def.category} · ${def.kind}`,
      keywords: kws.map((k) => ({
        id: k.id,
        label: k.label,
        reminder: k.reminder,
      })),
      textKjv: def.textKjv,
      proclaimText: def.proclaimText,
      effectSummary: def.effectSummary,
      detailRows,
      studyLayout: true,
    });
  }

  private renderCard(card: CardInstance, game: Game): HTMLElement {
    const el = document.createElement("article");
    // Hand face is always "clean": image + verse ref + cost only.
    // Effects / verse text live in the fullscreen study view.
    el.className = `card category-${card.def.category} clean-face`;
    el.dataset.instanceId = card.instanceId;
    el.dataset.defId = card.def.id;
    const canDrag = game.status === "playing" && !game.paused;
    if (game.paused) el.classList.add("paused-block");
    if (this.queuedId === card.instanceId) el.classList.add("queued");
    if (game.isEmpowerHighlighted(card.def)) el.classList.add("empowered");
    if (card.justDrawn) {
      el.classList.add("deal-in");
      // Consume flag so re-renders don't re-play the animation
      card.justDrawn = false;
      // Drop the class after the run so hover transform CSS takes over cleanly
      const clear = () => el.classList.remove("deal-in");
      el.addEventListener("animationend", clear, { once: true });
      window.setTimeout(clear, 500);
    }
    // Pointer path only — CSS also sets -webkit-user-drag: none on hand cards.
    el.draggable = false;

    const aimHint = dragHintForCard(card.def);
    el.title = canDrag
      ? this.queuedId === card.instanceId
        ? "Queued — will proclaim when speech ends (or Cancel above)"
        : game.isProclaiming()
          ? `${aimHint} — drop queues until speech ends. Tap to study.`
          : `${aimHint}. Tap to study.`
      : "Paused — tap to study";

    const speak = card.def.speakSeconds ?? 2.5;
    const art = createCardArt({
      verseId: card.def.id,
      category: card.def.category,
      reference: card.def.reference,
      speakSeconds: Math.round(speak * 10) / 10,
      compactRef: true,
    });
    art.classList.add("card-zoomable");
    art.title = `${card.def.reference} — tap to study`;

    // Fallback when pointer-drag is not armed (paused / not playing).
    // While playing, study opens from pointerup on a non-drag tap — the
    // synthetic click is unreliable after setPointerCapture + hand rebuilds.
    const openFsClick = (e: Event) => {
      if (
        this.pointerDrag?.suppressClick ||
        performance.now() < this.suppressStudyUntil
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (this.pointerDrag) this.pointerDrag.suppressClick = false;
        return;
      }
      e.stopPropagation();
      this.openStudy(card.def);
    };
    art.addEventListener("click", openFsClick);

    // --- Pointer drag (mouse / touch / pen) — sole play path ---
    el.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      // Paused / not playing: leave the gesture to click → study
      if (game.status !== "playing" || game.paused) return;

      this.cancelPointerDrag(false);
      this.pointerDragGame = game;
      const rect = el.getBoundingClientRect();
      this.pointerDrag = {
        pointerId: e.pointerId,
        instanceId: card.instanceId,
        def: card.def,
        el,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
        homeSlot: el.parentElement,
        homeW: rect.width,
        homeH: rect.height,
        pointerX: e.clientX,
        pointerY: e.clientY,
        flyX: rect.left,
        flyY: rect.top,
        flyScale: 1,
        overHand: true,
        suppressClick: false,
      };
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      // Document capture: keep the gesture if the WebView re-targets the node.
      this.bindDocPointerListeners();
    });

    // WebViews that ignore touch-action still honor non-passive touchmove.
    // Only claim once past threshold so a pure tap still synthesizes click
    // as a backup study path when needed.
    el.addEventListener(
      "touchmove",
      (e) => {
        if (this.pointerDrag?.el === el && this.pointerDrag.active) {
          e.preventDefault();
        }
      },
      { passive: false },
    );

    // No foot: effects stay off the in-game face (study via fullscreen)
    el.append(art);
    return el;
  }

  private bindDocPointerListeners(): void {
    if (this.docPointerBound) return;
    this.docPointerBound = true;
    document.addEventListener("pointermove", this.onDocPointerMove, {
      passive: false,
      capture: true,
    });
    document.addEventListener("pointerup", this.onDocPointerUp, true);
    document.addEventListener("pointercancel", this.onDocPointerCancel, true);
  }

  private unbindDocPointerListeners(): void {
    if (!this.docPointerBound) return;
    this.docPointerBound = false;
    document.removeEventListener("pointermove", this.onDocPointerMove, true);
    document.removeEventListener("pointerup", this.onDocPointerUp, true);
    document.removeEventListener("pointercancel", this.onDocPointerCancel, true);
  }

  private handlePointerMove(e: PointerEvent): void {
    const pd = this.pointerDrag;
    if (!pd || pd.pointerId !== e.pointerId) return;

    const dx = e.clientX - pd.startX;
    const dy = e.clientY - pd.startY;

    if (!pd.active) {
      if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
      const g = this.pointerDragGame;
      if (g && (g.status !== "playing" || g.paused)) {
        this.cancelPointerDrag(false);
        return;
      }
      // Past threshold: claim the gesture so host chrome can't steal it.
      if (e.cancelable) e.preventDefault();
      pd.active = true;
      pd.suppressClick = true;
      this.dragId = pd.instanceId;
      pd.el.draggable = false;
      const home = this.isOverHand(e.clientX, e.clientY, true);
      pd.overHand = home;
      this.setHeldHome(pd, home, e.clientX, e.clientY);
      document.body.classList.add("card-pointer-dragging");
      this.handlers.onDragStart(pd.instanceId);
      this.handlers.onDragMove(e.clientX, e.clientY);
    } else {
      if (e.cancelable) e.preventDefault();
      const home = this.isOverHand(e.clientX, e.clientY, pd.overHand);
      pd.pointerX = e.clientX;
      pd.pointerY = e.clientY;
      if (home !== pd.overHand) {
        pd.overHand = home;
        this.setHeldHome(pd, home, e.clientX, e.clientY);
      }
      this.handlers.onDragMove(e.clientX, e.clientY);
    }
  }

  private endPointer(e: PointerEvent, dropped: boolean): void {
    const pd = this.pointerDrag;
    if (!pd || pd.pointerId !== e.pointerId) return;

    const wasActive = pd.active;
    const x = e.clientX;
    const y = e.clientY;
    const id = pd.instanceId;
    const def = pd.def;
    const el = pd.el;

    const overHand =
      wasActive && this.isOverHand(x, y, pd.overHand);
    this.teardownPointerDragVisual(pd);
    this.unbindDocPointerListeners();
    this.pointerDragGame = null;

    try {
      if (el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* ignore */
    }

    if (wasActive) {
      // Keep a short-lived suppressClick flag so the post-touch click is eaten
      this.pointerDrag = {
        pointerId: pd.pointerId,
        instanceId: id,
        def,
        el,
        startX: pd.startX,
        startY: pd.startY,
        active: false,
        homeSlot: pd.homeSlot,
        homeW: pd.homeW,
        homeH: pd.homeH,
        pointerX: x,
        pointerY: y,
        flyX: pd.flyX,
        flyY: pd.flyY,
        flyScale: pd.flyScale,
        overHand,
        suppressClick: true,
      };
      this.dragId = null;
      this.suppressStudyUntil = performance.now() + 400;
      // Lay aside: drop onto another hand card to choose it.
      if (dropped && overHand && def.layAside && this.handlers.onDropOnCard) {
        const other = this.cardAtPoint(x, y, id);
        if (other) {
          this.handlers.onDropOnCard(id, other);
          this.handlers.onDragEnd();
          window.setTimeout(() => {
            if (this.pointerDrag?.instanceId === id && !this.pointerDrag.active) {
              this.pointerDrag = null;
            }
          }, 400);
          return;
        }
      }
      // Release over the stack cancels — do not play.
      if (dropped && !overHand) {
        this.handlers.onDropAt(x, y, id);
      }
      this.handlers.onDragEnd();
      window.setTimeout(() => {
        if (this.pointerDrag?.instanceId === id && !this.pointerDrag.active) {
          this.pointerDrag = null;
        }
      }, 400);
    } else {
      // Tap (no drag): open study here. After pointer-only DnD, the
      // following click is often lost (capture + hand DOM rebuild).
      this.pointerDrag = null;
      this.openStudy(def);
      // Block the synthetic follow-up click so we don't open twice
      this.suppressStudyUntil = performance.now() + 400;
    }
  }

  private teardownPointerDragVisual(pd: PointerDragState): void {
    this.restoreCardToSlot(pd);
    document.body.classList.remove("card-pointer-dragging");
  }

  /** Hand dock, with a little hysteresis so the lift doesn't flicker. */
  private isOverHand(
    clientX: number,
    clientY: number,
    currentlyHome: boolean,
  ): boolean {
    const r = this.root.getBoundingClientRect();
    // Home: allow a bit of upward jitter on the lifted face.
    // Away: don't steal the Secret Place / Will strip just above the dock.
    const top = currentlyHome ? r.top - 18 : r.top + 6;
    return (
      clientX >= r.left &&
      clientX <= r.right &&
      clientY >= top &&
      clientY <= r.bottom + 10
    );
  }

  private setHeldHome(
    pd: PointerDragState,
    home: boolean,
    clientX: number,
    clientY: number,
  ): void {
    if (home) this.dockCard(pd);
    else this.launchCard(pd, clientX, clientY);
  }

  private launchCard(
    pd: PointerDragState,
    clientX: number,
    clientY: number,
  ): void {
    const el = pd.el;
    const rect = el.getBoundingClientRect();
    pd.pointerX = clientX;
    pd.pointerY = clientY;
    pd.flyX = rect.left;
    pd.flyY = rect.top;
    pd.flyScale = pd.homeW > 0 ? rect.width / pd.homeW : 1;
    el.classList.add("held", "drag-away", "card-in-flight");
    el.classList.remove("drag-home");
    if (el.parentElement !== document.body) {
      document.body.appendChild(el);
    }
    el.style.width = `${pd.homeW}px`;
    el.style.height = `${pd.homeH}px`;
    this.applyFlyTransform(pd);
    this.startFlyLoop();
  }

  private dockCard(pd: PointerDragState): void {
    this.stopFlyLoop();
    const el = pd.el;
    el.classList.add("held", "drag-home");
    el.classList.remove("drag-away", "card-in-flight");
    el.style.width = "";
    el.style.height = "";
    el.style.transform = "";
    if (pd.homeSlot && el.parentElement !== pd.homeSlot) {
      pd.homeSlot.appendChild(el);
    }
  }

  private restoreCardToSlot(pd: PointerDragState): void {
    this.stopFlyLoop();
    const el = pd.el;
    el.classList.remove("held", "drag-home", "drag-away", "card-in-flight");
    el.style.width = "";
    el.style.height = "";
    el.style.transform = "";
    if (pd.homeSlot && el.parentElement !== pd.homeSlot) {
      pd.homeSlot.appendChild(el);
    }
  }

  private flyTarget(pd: PointerDragState): { x: number; y: number } {
    return {
      x: pd.pointerX - pd.homeW * FLY_SCALE - FLY_GAP_X,
      y: pd.pointerY + FLY_GAP_Y,
    };
  }

  private applyFlyTransform(pd: PointerDragState): void {
    pd.el.style.transform = `translate(${pd.flyX}px, ${pd.flyY}px) scale(${pd.flyScale})`;
  }

  private startFlyLoop(): void {
    if (this.flyRaf) return;
    this.lastFlyTs = performance.now();
    const tick = (now: number) => {
      this.flyRaf = 0;
      const pd = this.pointerDrag;
      if (!pd?.active || pd.overHand) return;
      const dt = Math.min(0.05, (now - this.lastFlyTs) / 1000);
      this.lastFlyTs = now;
      const reduce =
        typeof matchMedia === "function" &&
        matchMedia("(prefers-reduced-motion: reduce)").matches;
      const k = reduce ? 1 : 1 - Math.exp(-dt / FLY_LERP_TAU);
      const target = this.flyTarget(pd);
      pd.flyX += (target.x - pd.flyX) * k;
      pd.flyY += (target.y - pd.flyY) * k;
      pd.flyScale += (FLY_SCALE - pd.flyScale) * k;
      this.applyFlyTransform(pd);
      this.flyRaf = requestAnimationFrame(tick);
    };
    this.flyRaf = requestAnimationFrame(tick);
  }

  private stopFlyLoop(): void {
    if (!this.flyRaf) return;
    cancelAnimationFrame(this.flyRaf);
    this.flyRaf = 0;
  }

  private cancelPointerDrag(dropped: boolean): void {
    const pd = this.pointerDrag;
    if (!pd) return;
    const wasActive = pd.active;
    this.teardownPointerDragVisual(pd);
    this.unbindDocPointerListeners();
    this.pointerDragGame = null;
    this.dragId = null;
    this.pointerDrag = null;
    if (wasActive) {
      this.handlers.onDragEnd();
    }
    void dropped;
  }
}
